// 2026-06-08 第一次登入歡迎 modal（共用元件）— 依 Sean 截圖二改版
//   sign-in / two-fa 兩處共用，傳 open / onClose / onExperience / onLearnMore
import { useContext } from "react";
import PropTypes from "prop-types";
import { Dialog, Box, Typography, Button, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { LanguageContext } from "../../../../context/LanguageContext";
import { COLORS, TYPO } from "theme/tokens";

// 內嵌 4 語系（modal 內容為改版新文字，self-contained）
const TEXT = {
  "zh-TW": {
    header: "歡迎加入",
    title: "歡迎加入 MAS 智能交易平台",
    sub1: "立即享有 15 天免費體驗",
    sub2: "開啟您的 AI 交易旅程！",
    intro: "您可以在這裡體驗到",
    f1: "AI 策略精靈", f2: "生成 AI 策略", f3: "回測與績效分析", f4: "雲端儲存與部署",
    learnMore: "了解更多方案", experience: "立即體驗",
  },
  "zh-CN": {
    header: "欢迎加入",
    title: "欢迎加入 MAS 智能交易平台",
    sub1: "立即享有 15 天免费体验",
    sub2: "开启您的 AI 交易旅程！",
    intro: "您可以在这里体验到",
    f1: "AI 策略精灵", f2: "生成 AI 策略", f3: "回测与绩效分析", f4: "云端储存与部署",
    learnMore: "了解更多方案", experience: "立即体验",
  },
  "en-US": {
    header: "Welcome",
    title: "Welcome to MAS Trading Platform",
    sub1: "Enjoy a 15-Day Free Trial",
    sub2: "Start your AI trading journey!",
    intro: "Here's what you can explore",
    f1: "AI Strategy Genie", f2: "Generate AI Strategies", f3: "Backtest & Performance", f4: "Cloud Storage & Deploy",
    learnMore: "Learn More Plans", experience: "Get Started",
  },
  "en-GB": {
    header: "Welcome",
    title: "Welcome to MAS Trading Platform",
    sub1: "Enjoy a 15-Day Free Trial",
    sub2: "Start your AI trading journey!",
    intro: "Here's what you can explore",
    f1: "AI Strategy Genie", f2: "Generate AI Strategies", f3: "Backtest & Performance", f4: "Cloud Storage & Deploy",
    learnMore: "Learn More Plans", experience: "Get Started",
  },
};

export default function WelcomeModal({ open, onClose, onExperience, onLearnMore }) {
  const { language } = useContext(LanguageContext);
  const L = TEXT[language] || TEXT["zh-TW"];

  const features = [
    { img: "/images/icon/login/Container.png", label: L.f1 },
    { img: "/images/icon/login/Icon1.png", label: L.f2 },
    { img: "/images/icon/login/Icon2.png", label: L.f3 },
    { img: "/images/icon/login/Icon3.png", label: L.f4 },
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: 700,
          maxWidth: 700,
          borderRadius: "24px",
          boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
          m: 2,
        },
      }}
    >
      {/* === 上部分：標題 + 關閉 === */}
      <Box
        sx={{
          p: 3,
          borderBottom: `1px solid ${COLORS.grayLighter}`,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography sx={{ ...TYPO.displayM, color: COLORS.black, fontWeight: 500 }}>{L.header}</Typography>
        <IconButton onClick={onClose} size="small" sx={{ color: COLORS.grayDark }}>
          <CloseIcon />
        </IconButton>
      </Box>

      {/* === 中部分：歡迎內容 === */}
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "30px", px: 3, py: 4 }}>
        <Typography sx={{ ...TYPO.displayM, color: COLORS.black, fontWeight: 700, textAlign: "center" }}>
          {L.title}
        </Typography>
        <Box
          component="img"
          src="/images/icon/login/Icon%20Container.png"
          alt=""
          sx={{ width: 64, height: 64, objectFit: "contain" }}
        />
        <Typography sx={{ ...TYPO.title, color: COLORS.main, fontWeight: 700, textAlign: "center", whiteSpace: "pre-line", lineHeight: 1.4 }}>
          {`${L.sub1}\n${L.sub2}`}
        </Typography>
        <Typography sx={{ ...TYPO.title, color: COLORS.black, fontWeight: 500, textAlign: "center" }}>
          {L.intro}
        </Typography>
        <Box sx={{ display: "flex", gap: 1.5, width: "100%" }}>
          {features.map((f, i) => (
            <Box
              key={i}
              sx={{
                flex: 1,
                border: `1px solid ${COLORS.grayLighter}`,
                borderRadius: "12px",
                p: 1.5,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
                textAlign: "center",
                minHeight: 96,
              }}
            >
              <Box component="img" src={f.img} alt="" sx={{ width: 28, height: 28, objectFit: "contain" }} />
              <Typography sx={{ ...TYPO.bodyM, color: COLORS.black, fontWeight: 400, lineHeight: 1.3 }}>
                {f.label}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* === 下部分：按鈕 === */}
      <Box sx={{ px: 3, pt: 3, pb: 3, borderTop: `1px solid ${COLORS.grayLighter}`, display: "flex", justifyContent: "flex-end", gap: "10px" }}>
        <Button
          disableElevation
          onClick={onLearnMore}
          sx={{
            px: 4,
            py: 1.5,
            borderRadius: "8px",
            bgcolor: COLORS.grayLightest,
            ...TYPO.bodyM,
            fontWeight: 500,
            color: COLORS.black,
            textTransform: "none",
            whiteSpace: "nowrap",
            "&:hover": { bgcolor: COLORS.grayLighter, color: COLORS.black },
          }}
        >
          {L.learnMore}
        </Button>
        <Button
          disableElevation
          onClick={onExperience}
          sx={{
            px: 4,
            py: 1.5,
            minWidth: 200,
            borderRadius: "8px",
            bgcolor: COLORS.main,
            ...TYPO.bodyM,
            fontWeight: 500,
            color: COLORS.white,
            textTransform: "none",
            "&:hover": { bgcolor: COLORS.main, color: COLORS.white },
          }}
        >
          {L.experience}
        </Button>
      </Box>
    </Dialog>
  );
}

WelcomeModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onExperience: PropTypes.func,
  onLearnMore: PropTypes.func,
};
